import { useState, useEffect, useCallback, useRef } from 'react';
import KohdeTabBar from './KohdeTabBar';
import TaulukkoFooter from './TaulukkoFooter';
import { JULKISIVU_RAKENNEOSA_OPTIONS } from '../../../assets/julkisivuRakenneosaOptions';
import { useReportTemplateContext } from '../ReportTemplate/ReportTemplateContext';
import { useRakennusData } from '../../../context/RakennusDataContext';
import { defaultProjectName } from '../../../utils/projectNaming';
import { mittausCacheSet, mittausCacheSubscribe, subscribeNayteTunnus } from '../../../utils/mittausCache';
import { findLinkedProjectId } from '../../../utils/ptsUtils';
import {
  getDynamicPTSProjects,
  getMittausProjektitByPTS,
  createMittausProjekti,
  getMittausProjekti,
  renameMittausProjekti,
  deleteMittausProjekti,
  updateMittausProjektiMeta,
  createMittausKohde,
  updateMittausKohde,
  deleteMittausKohde,
} from '../../../api/api';

const newRow = () => ({ id: crypto.randomUUID(), tunnus: '', kloridipitoisuus: '' });

const C  = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.9rem', verticalAlign: 'middle' };
const CH = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.9rem', width: '100%', background: '#fff' };

export default function KloridipitoisuusBlock({ data, onChange, card, tableStartNumber, subsectionTitle, onCreateNayte, ptsProjectName, allViewValue, onSetAllView }) {
  let ctxKiinteisto = null;
  let ctxSections = null;
  try {
    const ctx = useReportTemplateContext();
    ctxKiinteisto = ctx?.report?.id_kiinteisto ?? ctx?.rakennusData?.id_kiinteisto ?? null;
    ctxSections = ctx?.sections ?? null;
  } catch {}
  const { rakennusData } = useRakennusData?.() ?? {};
  const id_kiinteisto = ctxKiinteisto ?? rakennusData?.id_kiinteisto ?? null;
  const rakennukset = card?.rakennukset_fulls ?? [];
  const linkedPtsId = findLinkedProjectId(ctxSections);

  const [projekt, setProjekt]             = useState(null);
  const [allProjektit, setAllProjektit]   = useState([]);
  const [activeKohdeId, setActiveKohdeId] = useState(null);
  const [loading, setLoading]             = useState(false);
  const allKohteet = allViewValue ?? true;
  const [nayteCreateError, setNayteCreateError] = useState(null);
  const [isMobile, setIsMobile]           = useState(() => window.innerWidth < 640);
  const [addModal, setAddModal]                         = useState(false);
  const [addModalRakenneosa, setAddModalRakenneosa]     = useState('');
  const [addModalTunnus, setAddModalTunnus]             = useState('');
  const [addModalCreating, setAddModalCreating]         = useState(false);

  const kohteet     = projekt?.kohteet ?? [];
  const activeKohde = kohteet.find(k => k.id === activeKohdeId) ?? null;
  const activeIdx   = kohteet.findIndex(k => k.id === activeKohdeId);

  const [showSelector, setShowSelector]           = useState(false);
  const [ptsProjects, setPtsProjects]             = useState([]);
  const [selectedPtsId, setSelectedPtsId]         = useState(null);
  const [existingProjektit, setExistingProjektit] = useState([]);
  const [newProjName, setNewProjName]             = useState('');
  const [newProjDate, setNewProjDate]             = useState('');
  const [newProjScope, setNewProjScope]           = useState('property');
  const [newProjRakennusId, setNewProjRakennusId] = useState(null);
  const [projNameError, setProjNameError]         = useState('');
  const [apiError, setApiError]                   = useState('');
  const [selectorTab, setSelectorTab]             = useState('new');
  const [pendingDeleteProject, setPendingDeleteProject] = useState(false);
  const [pendingDeleteKohdeId, setPendingDeleteKohdeId] = useState(null);
  const [renamingProject, setRenamingProject]           = useState(false);
  const [renameProjectValue, setRenameProjectValue]     = useState('');

  const saveTimers = useRef({});
  const scheduleSave = useCallback((kohdeId, payload) => {
    clearTimeout(saveTimers.current[kohdeId]);
    saveTimers.current[kohdeId] = setTimeout(() => {
      updateMittausKohde(kohdeId, payload).catch(console.error);
    }, 1200);
  }, []);

  useEffect(() => {
    if (!data?.mittausProjektiId) return;
    setLoading(true);
    getMittausProjekti(data.mittausProjektiId)
      .then(res => {
        const proj = res.data;
        setProjekt(proj);
        mittausCacheSet(proj.id, proj);
        setActiveKohdeId((proj.kohteet ?? [])[0]?.id ?? null);
        const ptsId = data?.ptsProjectId ?? proj.id_pts_project;
        if (ptsId) {
          if (!data?.ptsProjectId) onChange?.({ ...data, ptsProjectId: ptsId });
          getMittausProjektitByPTS(ptsId, 'kloridi').then(r => setAllProjektit(r.data ?? [])).catch(() => {});
        }
      })
      .catch(() => setProjekt(null))
      .finally(() => setLoading(false));
  }, [data?.mittausProjektiId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ptsId = data?.ptsProjectId;
    if (!ptsId) return;
    getMittausProjektitByPTS(ptsId, 'kloridi').then(res => {
      const list = res.data ?? [];
      setAllProjektit(list);
      if (list.length > 0 && !data?.mittausProjektiId) {
        setLoading(true);
        getMittausProjekti(list[0].id).then(r => {
          setProjekt(r.data);
          mittausCacheSet(r.data.id, r.data);
          setActiveKohdeId((r.data.kohteet ?? [])[0]?.id ?? null);
          onChange?.({ ...data, mittausProjektiId: r.data.id });
        }).catch(() => {}).finally(() => setLoading(false));
      }
    }).catch(() => {});
  }, [data?.ptsProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data?.mittausProjektiId) return;
    return mittausCacheSubscribe(data.mittausProjektiId, (updatedProj) => {
      setProjekt(updatedProj);
      setActiveKohdeId(prev => prev ?? updatedProj.kohteet?.[0]?.id ?? null);
    });
  }, [data?.mittausProjektiId]);

  useEffect(() => {
    if (!id_kiinteisto) return;
    getDynamicPTSProjects(id_kiinteisto).then(res => setPtsProjects(res.data ?? [])).catch(() => {});
  }, [id_kiinteisto]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!linkedPtsId || linkedPtsId === data?.ptsProjectId) return;
    onChange?.({ ...data, ptsProjectId: linkedPtsId, mittausProjektiId: null });
  }, [linkedPtsId, data?.ptsProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    return subscribeNayteTunnus((nayteId, newTunnus) => {
      setProjekt(prev => {
        if (!prev) return prev;
        let changed = false;
        const kohteet = prev.kohteet.map(k => {
          if (k.nayte_id !== nayteId) return k;
          changed = true;
          const rows = (k.data ?? []).map((r, i) => i === 0 ? { ...r, tunnus: newTunnus } : r);
          updateMittausKohde(k.id, { data: rows }).catch(console.error);
          return { ...k, data: rows };
        });
        return changed ? { ...prev, kohteet } : prev;
      });
    });
  }, []);

  // Auto-add one initial row when a newly-created kohde has no data
  useEffect(() => {
    if (loading || !activeKohde || (activeKohde.data ?? []).length > 0) return;
    setProjekt(prev => ({
      ...prev,
      kohteet: (prev?.kohteet ?? []).map(k => {
        if (k.id !== activeKohdeId || (k.data ?? []).length > 0) return k;
        const d = [newRow()];
        updateMittausKohde(k.id, { data: d }).catch(() => {});
        return { ...k, data: d };
      }),
    }));
  }, [loading, activeKohdeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchToProject = async (id) => {
    setLoading(true);
    getMittausProjekti(id).then(res => {
      setProjekt(res.data);
      mittausCacheSet(res.data.id, res.data);
      setActiveKohdeId((res.data.kohteet ?? [])[0]?.id ?? null);
      onChange?.({ ...data, mittausProjektiId: id });
    }).catch(() => {}).finally(() => setLoading(false));
  };

  const refreshAllProjektit = (ptsId) =>
    getMittausProjektitByPTS(ptsId, 'kloridi')
      .then(res => setAllProjektit(res.data ?? []))
      .catch(() => {});

  const suggestedName = (scope, rakennusId, date) => {
    if (scope === 'building') {
      const r = rakennukset.find(r => r.id_rakennus === rakennusId) ?? rakennukset[0];
      const tunnus = r?.rakennustunnus ?? '';
      return tunnus ? `Rakennus ${tunnus} ${date}` : `Rakennus ${date}`;
    }
    const addr = String(rakennukset[0]?.osoite ?? card?.osoite ?? '').split('\n')[0].trim();
    return addr ? `${addr} ${date}` : defaultProjectName('Kloridipitoisuus', subsectionTitle);
  };

  const openSelector = async () => {
    if (!id_kiinteisto) return;
    const today = new Date().toISOString().split('T')[0];
    const rid = rakennukset[0]?.id_rakennus ?? null;
    setNewProjDate(today);
    setNewProjScope('property');
    setNewProjRakennusId(rid);
    setNewProjName(defaultProjectName('Kloridipitoisuus', subsectionTitle));
    setProjNameError('');
    setApiError('');
    setSelectorTab('new');
    try {
      const res = await getDynamicPTSProjects(id_kiinteisto);
      const pts = res.data ?? [];
      setPtsProjects(pts);
      setSelectedPtsId(pts[0]?.id ?? null);
      setExistingProjektit([]);
      if (pts[0]?.id) {
        const r2 = await getMittausProjektitByPTS(pts[0].id, 'kloridi');
        setExistingProjektit(r2.data ?? []);
      }
      setShowSelector(true);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Lataus epäonnistui');
    }
  };

  const onPtsChange = async (ptsId) => {
    setSelectedPtsId(ptsId);
    try {
      const res = await getMittausProjektitByPTS(ptsId, 'kloridi');
      setExistingProjektit(res.data ?? []);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Lataus epäonnistui');
    }
  };

  const createProject = async () => {
    if (!selectedPtsId) return;
    const name = newProjName.trim();
    if (!name) { setProjNameError('Nimi vaaditaan.'); return; }
    setProjNameError('');
    setApiError('');
    try {
      const res = await createMittausProjekti(selectedPtsId, 'kloridi', name, {
        paivamaara: newProjDate || null,
        scope: newProjScope,
        id_rakennus: newProjScope === 'building' ? (newProjRakennusId ?? null) : null,
      });
      const proj = res.data;
      setProjekt(proj);
      mittausCacheSet(proj.id, proj);
      setActiveKohdeId((proj.kohteet ?? [])[0]?.id ?? null);
      onChange?.({ ...data, mittausProjektiId: proj.id, ptsProjectId: selectedPtsId });
      refreshAllProjektit(selectedPtsId);
      setShowSelector(false);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Tarkastuskohteen luonti epäonnistui');
    }
  };

  const linkProject = async (id) => {
    setApiError('');
    try {
      const res = await getMittausProjekti(id);
      const proj = res.data;
      setProjekt(proj);
      mittausCacheSet(proj.id, proj);
      setActiveKohdeId((proj.kohteet ?? [])[0]?.id ?? null);
      onChange?.({ ...data, mittausProjektiId: id, ptsProjectId: selectedPtsId });
      refreshAllProjektit(selectedPtsId);
      setShowSelector(false);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Tarkastuskohteen lataus epäonnistui');
    }
  };

  const commitRenameProject = async () => {
    const trimmed = renameProjectValue.trim();
    if (!trimmed || !projekt) { setRenamingProject(false); return; }
    await renameMittausProjekti(projekt.id, trimmed);
    setProjekt(p => ({ ...p, nimi: trimmed }));
    setRenamingProject(false);
  };

  const confirmDeleteProject = async () => {
    if (!projekt) return;
    await deleteMittausProjekti(projekt.id);
    setProjekt(null);
    setActiveKohdeId(null);
    onChange?.({ ...data, mittausProjektiId: null });
    setPendingDeleteProject(false);
  };

  const openAddModal = () => { setAddModalRakenneosa(''); setAddModalTunnus(''); setAddModal(true); };
  const closeAddModal = () => { if (!addModalCreating) setAddModal(false); };
  const submitAddModal = async () => {
    const name = addModalRakenneosa.trim();
    if (!name || !addModalTunnus.trim()) return;
    setAddModalCreating(true);
    try { await handleKohdeCreate(name, false, addModalTunnus.trim()); setAddModal(false); }
    finally { setAddModalCreating(false); }
  };

  const handleKohdeCreate = async (name, withNayte = false, tunnus = '') => {
    let nayteId = null;
    if (withNayte && onCreateNayte) {
      try { const n = await onCreateNayte(name, tunnus, 'Kloridi'); nayteId = n?.id ?? null; }
      catch (err) { if (err?.code === 'DUPLICATE_TUNNUS') { setNayteCreateError(err.message); return null; } }
    }
    const res = await createMittausKohde(projekt.id, name, projekt.kohteet?.length ?? 0, nayteId);
    const kohde = { ...res.data, nayte_id: nayteId, data: [{ ...newRow(), ...(tunnus ? { tunnus } : {}) }] };
    await updateMittausKohde(kohde.id, { data: kohde.data });
    const updatedProjekt = { ...projekt, kohteet: [...(projekt.kohteet ?? []), kohde] };
    setProjekt(updatedProjekt);
    mittausCacheSet(updatedProjekt.id, updatedProjekt);
    setActiveKohdeId(kohde.id);
    return kohde;
  };

  const handleKohdeRename = async (id, name) => {
    await updateMittausKohde(id, { nimi: name });
    setProjekt(p => ({ ...p, kohteet: p.kohteet.map(k => k.id === id ? { ...k, nimi: name } : k) }));
  };

  const handleKohdeDelete = async (id) => {
    await deleteMittausKohde(id);
    const remaining = (projekt?.kohteet ?? []).filter(k => k.id !== id);
    setProjekt(p => ({ ...p, kohteet: remaining }));
    if (activeKohdeId === id) setActiveKohdeId(remaining[0]?.id ?? null);
  };

  const updateRow = (kohdeId, rowId, field, value) => {
    setProjekt(prev => {
      const updated = prev.kohteet.map(k => {
        if (k.id !== kohdeId) return k;
        const newRows = (k.data ?? []).map(r => r.id === rowId ? { ...r, [field]: value } : r);
        scheduleSave(k.id, { data: newRows });
        return { ...k, data: newRows };
      });
      return { ...prev, kohteet: updated };
    });
  };

  const addRow = (kohdeId) => {
    setProjekt(prev => {
      const updated = prev.kohteet.map(k => {
        if (k.id !== kohdeId) return k;
        const newRows = [...(k.data ?? []), newRow()];
        scheduleSave(k.id, { data: newRows });
        return { ...k, data: newRows };
      });
      return { ...prev, kohteet: updated };
    });
  };

  const removeRow = (kohdeId, rowId) => {
    setProjekt(prev => {
      const updated = prev.kohteet.map(k => {
        if (k.id !== kohdeId) return k;
        let newRows = (k.data ?? []).filter(r => r.id !== rowId);
        if (!newRows.length) newRows = [newRow()];
        scheduleSave(k.id, { data: newRows });
        return { ...k, data: newRows };
      });
      return { ...prev, kohteet: updated };
    });
  };


  if (!id_kiinteisto) return (
    <div>
      <div className="text-muted small p-2">Kiinteistön ID ei saatavilla.</div>
      <TaulukkoFooter taulukkoNum={tableStartNumber ?? 1} kohdeId="preview" caption={null} onChange={() => {}} />
    </div>
  );
  if (loading) return <div className="text-muted small p-2">Ladataan…</div>;

  return (
    <div style={{ fontSize: '0.95rem' }}>

      {(ptsProjectName || data?.ptsProjectId) && (
        <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: 6 }}>
          {ptsProjectName
            ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.name
            ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.nimi
            ?? `PTS ${data.ptsProjectId}`}
        </div>
      )}
      {/* ── Project header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Tarkastuskohde:</span>
        {allProjektit.length > 0 ? (
          <>
            {allProjektit.map(p => (
              <button key={p.id} type="button" onClick={() => switchToProject(p.id)}
                style={{ padding: '2px 8px', fontSize: '0.78rem', border: '1px solid', borderRadius: 3, cursor: 'pointer',
                  borderColor: p.id === projekt?.id ? '#198754' : '#dee2e6',
                  background: p.id === projekt?.id ? '#f0fdf4' : '#fff',
                  color: p.id === projekt?.id ? '#15803d' : '#6c757d',
                  fontWeight: p.id === projekt?.id ? 600 : 400 }}>
                {p.nimi}
              </button>
            ))}
            {projekt && (
              <button type="button" onClick={() => setPendingDeleteProject(true)}
                style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'} title="Poista aktiivinen tarkastuskohde">×</button>
            )}
          </>
        ) : projekt ? (
          <>
            {renamingProject ? (
              <input autoFocus value={renameProjectValue} onChange={e => setRenameProjectValue(e.target.value)}
                onBlur={commitRenameProject}
                onKeyDown={e => { if (e.key === 'Enter') commitRenameProject(); if (e.key === 'Escape') setRenamingProject(false); }}
                style={{ fontSize: '0.82rem', fontWeight: 700, border: 'none', borderBottom: '1px solid #198754', outline: 'none', background: 'transparent', padding: '0 2px' }} />
            ) : (
              <span style={{ fontWeight: 600, fontSize: '0.82rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onDoubleClick={() => { setRenameProjectValue(projekt.nimi); setRenamingProject(true); }}>
                {projekt.nimi}
              </span>
            )}
            <button type="button" onClick={() => setPendingDeleteProject(true)}
              style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'} title="Poista tarkastuskohde">×</button>
          </>
        ) : (
          <button type="button" onClick={openSelector}
            style={{ padding: '3px 10px', borderRadius: 20, border: '1px dashed #0d6efd', background: '#fff', color: '#0d6efd', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}>
            + Valitse / luo tarkastuskohde
          </button>
        )}
      </div>

      {projekt && (
        <>
          {/* ── Kohde tabs + toggle ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <KohdeTabBar
                kohteet={kohteet}
                activeKohdeId={activeKohdeId}
                tableStartNumber={tableStartNumber}
                onActivate={id => { setActiveKohdeId(id); onSetAllView?.(false); }}
                onAdd={openAddModal}
                addLabel="+ Lisää näyte"
                onRename={handleKohdeRename}
                onDelete={handleKohdeDelete}
                hideTabs={allKohteet}
                displayNimi={k => [k.nimi, k.data?.[0]?.tunnus].filter(Boolean).join(' — ')}
              />
            </div>
            <button
              type="button"
              onClick={() => onSetAllView?.(!allKohteet)}
              title={allKohteet ? 'Näytä välilehdet' : 'Näytä kaikki rinnakkain'}
              style={{ flexShrink: 0, padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4, background: allKohteet ? '#198754' : '#fff', color: allKohteet ? '#fff' : '#6c757d', fontSize: '1rem', cursor: 'pointer', marginTop: 2 }}
            >⇄</button>
          </div>

          {addModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={closeAddModal}>
              <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(520px, 95vw)', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Lisää näyte</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d', marginBottom: 8 }}>Rakenneosa <span style={{ color: '#dc3545' }}>*</span></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
                      {JULKISIVU_RAKENNEOSA_OPTIONS.map(opt => {
                        const sel = addModalRakenneosa === opt;
                        return (
                          <button key={opt} type="button" onClick={() => setAddModalRakenneosa(opt)}
                            style={{ padding: '5px 12px', borderRadius: 20, border: '1.5px solid', fontSize: '0.82rem', cursor: 'pointer', textAlign: 'left',
                              fontWeight: sel ? 700 : 400, borderColor: sel ? '#198754' : '#dee2e6',
                              background: sel ? '#f0fdf4' : '#fafafa', color: sel ? '#15803d' : '#495057' }}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    <input type="text" placeholder="Tai kirjoita…" value={addModalRakenneosa}
                      onChange={e => setAddModalRakenneosa(e.target.value)}
                      style={{ width: '100%', border: '1px solid #ced4da', borderRadius: 4, padding: '5px 9px', fontSize: '0.85rem', outline: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignSelf: 'stretch' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d', marginBottom: 8 }}>Näytetunnus <span style={{ color: '#dc3545' }}>*</span></div>
                    <input autoFocus type="text" placeholder="esim. Kl1" value={addModalTunnus}
                      onChange={e => setAddModalTunnus(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && addModalRakenneosa.trim() && addModalTunnus.trim()) submitAddModal(); if (e.key === 'Escape') closeAddModal(); }}
                      style={{ width: '100%', border: '1.5px solid #ced4da', borderRadius: 6, padding: '10px 14px', fontSize: '1.1rem', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#6c757d', marginRight: 'auto' }}><span style={{ color: '#dc3545' }}>*</span> Pakollinen tieto</span>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeAddModal} disabled={addModalCreating}>Peruuta</button>
                  <button type="button" className="btn btn-sm btn-success" onClick={submitAddModal} disabled={addModalCreating || !addModalRakenneosa.trim() || !addModalTunnus.trim()}>
                    {addModalCreating ? <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} /> : 'Lisää'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {nayteCreateError && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setNayteCreateError(null)}>
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 92vw)', padding: '28px 28px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>⚠️</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>Näytetunnus on jo olemassa</div>
                <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.5 }}>Voit lisätä testejä näytteelle poranäyte taulukosta.</div>
                <button type="button" className="btn btn-warning btn-sm mt-1" onClick={() => setNayteCreateError(null)} style={{ minWidth: 80 }}>OK</button>
              </div>
            </div>
          )}

          {/* ── Table / Cards ── */}
          {(() => {
            const visibleKohteet = allKohteet ? kohteet : (activeKohde ? [activeKohde] : []);
            if (!visibleKohteet.length) return null;
            const LBL = { fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 };
            if (isMobile) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {visibleKohteet.map(k => (
                    <div key={k.id} style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 12px', background: '#fafafa' }}>
                      {allKohteet && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #e9ecef' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>{k.nimi}</span>
                          <button type="button" onClick={() => setPendingDeleteKohdeId(k.id)}
                            style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1.1rem', padding: 0, lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'}
                            title="Poista rakenneosa">×</button>
                        </div>
                      )}
                      {(k.data ?? []).map((row, ri) => (
                        <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, ...(ri > 0 ? { marginTop: 8, paddingTop: 8, borderTop: '1px solid #e9ecef' } : {}) }}>
                          <div>
                            <div style={LBL}>Tunnus</div>
                            {k.nayte_id == null
                              ? <input style={{ ...inp, textAlign: 'center' }} type="text" value={row.tunnus ?? ''} onChange={e => updateRow(k.id, row.id, 'tunnus', e.target.value)} placeholder="esim. Kl1" />
                              : <div style={{ fontSize: '0.9rem', fontWeight: 500, padding: '3px 0', color: row.tunnus ? '#212529' : '#adb5bd', fontStyle: row.tunnus ? 'normal' : 'italic' }}>{row.tunnus || '—'}</div>}
                          </div>
                          <div>
                            <div style={LBL}>Kloridipitoisuus %</div>
                            <input style={{ ...inp, textAlign: 'center' }} value={row.kloridipitoisuus ?? ''} onChange={e => updateRow(k.id, row.id, 'kloridipitoisuus', e.target.value)} placeholder="0,02" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            }
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                  <colgroup>
                    {allKohteet && <col style={{ width: '25%' }} />}
                    <col style={{ width: allKohteet ? '25%' : '33%' }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      {allKohteet && <th style={CH}>Rakenneosa</th>}
                      <th style={CH}>Tunnus</th>
                      <th style={CH}>Kloridipitoisuus betonin painosta (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleKohteet.map(k =>
                      (k.data ?? []).map((row, ri) => (
                        <tr key={row.id}>
                          {allKohteet && ri === 0 && (
                            <td style={{ ...C, fontWeight: 600, fontSize: '0.8rem', color: '#495057', verticalAlign: 'top', textAlign: 'center' }}
                              rowSpan={(k.data ?? []).length}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                <span>{k.nimi}</span>
                                <button type="button" onClick={() => setPendingDeleteKohdeId(k.id)}
                                  style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1, flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'}
                                  title="Poista rakenneosa">×</button>
                              </div>
                            </td>
                          )}
                          <td style={C}>{k.nayte_id == null
                            ? <input style={{ ...inp, textAlign: 'center' }} type="text" value={row.tunnus ?? ''} onChange={e => updateRow(k.id, row.id, 'tunnus', e.target.value)} placeholder="esim. Kl1" />
                            : <span style={{ fontWeight: 500 }}>{row.tunnus || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</span>}</td>
                          <td style={{ ...C, textAlign: 'center' }}>
                            <input style={{ ...inp, textAlign: 'center' }} value={row.kloridipitoisuus ?? ''} onChange={e => updateRow(k.id, row.id, 'kloridipitoisuus', e.target.value)} placeholder="0,02" />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}

        </>
      )}

      <TaulukkoFooter
        taulukkoNum={tableStartNumber != null ? tableStartNumber + Math.max(0, activeIdx) : Math.max(0, activeIdx) + 1}
        caption={data?.caption}
        onChange={capData => onChange?.({ ...data, caption: capData })}
      />

      {/* ── Project selector overlay ── */}
      {showSelector && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowSelector(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(480px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Kloridipitoisuus-tarkastuskohde</div>
            <div>
              <label style={{ fontSize: '0.82rem', color: '#6c757d', fontWeight: 600, marginBottom: 4, display: 'block' }}>Dynamic PTS -projekti</label>
              <select className="form-select form-select-sm" value={selectedPtsId ?? ''} onChange={e => onPtsChange(Number(e.target.value))}>
                {ptsProjects.map(p => <option key={p.id} value={p.id}>{p.name ?? p.nimi}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6' }}>
              {['new', 'existing'].map(tab => (
                <button key={tab} type="button" onClick={() => setSelectorTab(tab)}
                  style={{ padding: '6px 16px', border: 'none', borderBottom: tab === selectorTab ? '2px solid #0d6efd' : '2px solid transparent', background: 'none', color: tab === selectorTab ? '#0d6efd' : '#6c757d', fontWeight: tab === selectorTab ? 600 : 400, fontSize: '0.88rem', cursor: 'pointer' }}>
                  {tab === 'new' ? 'Uusi tarkastuskohde' : `Olemassa olevat (${existingProjektit.length})`}
                </button>
              ))}
            </div>
            {selectorTab === 'new' ? (
              <>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Tarkastuskohteen nimi</label>
                  <input className="form-control form-control-sm" autoFocus value={newProjName}
                    onChange={e => { setNewProjName(e.target.value); setProjNameError(''); }}
                    onKeyDown={e => e.key === 'Enter' && newProjName.trim() && createProject()}
                    style={{ borderColor: projNameError ? '#dc3545' : undefined }} />
                  {projNameError && <div style={{ fontSize: '0.7rem', color: '#dc3545', marginTop: 3 }}>{projNameError}</div>}
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Päivämäärä</label>
                  <input className="form-control form-control-sm" type="date" value={newProjDate} onChange={e => setNewProjDate(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 6 }}>Laajuus</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['property', 'Koko kiinteistö'], ['building', 'Yksittäinen rakennus']].map(([scope, label]) => (
                      <button key={scope} type="button"
                        onClick={() => { setNewProjScope(scope); setNewProjName(suggestedName(scope, newProjRakennusId, newProjDate)); setProjNameError(''); }}
                        style={{ flex: 1, padding: '7px 12px', border: '1px solid', borderRadius: 4, borderColor: newProjScope === scope ? '#198754' : '#ced4da', background: newProjScope === scope ? '#198754' : '#fff', color: newProjScope === scope ? '#fff' : '#495057', fontSize: '0.82rem', cursor: 'pointer', fontWeight: newProjScope === scope ? 600 : 400 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {newProjScope === 'building' && rakennukset.length > 0 && (
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Rakennus</label>
                    <select className="form-select form-select-sm" value={newProjRakennusId ?? ''}
                      onChange={e => { const rid = parseInt(e.target.value); setNewProjRakennusId(rid); setNewProjName(suggestedName('building', rid, newProjDate)); setProjNameError(''); }}>
                      {rakennukset.map(r => <option key={r.id_rakennus} value={r.id_rakennus}>{[r.rakennustunnus, r.osoite, r.toimipaikka].filter(Boolean).join(' — ') || `Rakennus ${r.id_rakennus}`}</option>)}
                    </select>
                  </div>
                )}
                {apiError && (
                  <div style={{ fontSize: '0.78rem', color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px' }}>
                    {apiError}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Peruuta</button>
                  <button className="btn btn-sm btn-success" onClick={createProject} disabled={!selectedPtsId || !newProjName.trim()}>Luo tarkastuskohde</button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {existingProjektit.length === 0
                  ? <div style={{ fontSize: '0.88rem', color: '#6c757d' }}>Ei kloridipitoisuus-tarkastuskohteita tässä PTS-projektissa.</div>
                  : existingProjektit.map(p => (
                    <button key={p.id} type="button" onClick={() => linkProject(p.id)}
                      style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p.nimi}
                    </button>
                  ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Sulje</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete project confirmation ── */}
      {pendingDeleteProject && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteProject(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista tarkastuskohde</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>Haluatko poistaa tarkastuskohteen <strong>{projekt?.nimi}</strong> kaikkine kohteineen?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteProject(false)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={confirmDeleteProject}>Poista</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete kohde confirmation ── */}
      {pendingDeleteKohdeId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteKohdeId(null); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista näyte</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>Haluatko poistaa näytteen <strong>{projekt?.kohteet?.find(k => k.id === pendingDeleteKohdeId)?.nimi}</strong>?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteKohdeId(null)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={async () => { const id = pendingDeleteKohdeId; setPendingDeleteKohdeId(null); await handleKohdeDelete(id); }}>Poista</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
